import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import UserAvatar from "app/components/UserAvatar";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import Button from "app/components/Button/ButtonVariant";
import { useActions } from "app/utils/hooks";
import { clear, fetchTimezone } from "app/actions/timezone";
import AccountPhoneVerificationModal from "app/components/AccountPhoneVerificationModal";
import upload from "app/utils/uploader";
import { editProfileSettings, fetchSelf } from "app/actions/user";
import AccountWrap from "..";
import { Toast } from "app/components/helpers";
import UserModel from "app/models/UserModel";
import i18n from "app/i18n";
import { hasAcademyPlan } from "app/utils/subscription";
import CheckBox from "app/components/inputs/CheckBox";
import { Loading } from "app/components/Wrappers";
import SportInput from "./SportInput";

const ProfileEdit = () => {
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const timezones = useSelector(
    (state: RootState) => state.timezone.data,
    shallowEqual,
  );
  const timeZoneAction = useActions({ fetchTimezone, clear });
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState<boolean>(false);
  const [userState, setUserState] = useState<UserModel>({
    avatar: self?.avatar,
    sport: self?.sport,
    autoCreateSpacesOptIn: self?.autoCreateSpacesOptIn,
    trackingOptIn: self?.trackingOptIn,
    assignTagsToResources: self?.assignTagsToResources,
    cancelOutboundInvitationEmails: self?.cancelOutboundInvitationEmails,
  });
  const [uploadedAvatar, setUploadedAvatar] = useState(null);
  const [toastAlert, setToastAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [toastErrorAlert, setToastErrorrAlert] = useState(null);
  const [errors, setErrors] = useState([]);
  const [sportError, setSportError] = useState(false);

  const languages = useMemo(
    () => [
      { label: t("English"), value: "en" },
      { label: t("Japanese"), value: "ja" },
    ],
    [i18n.language],
  );

  useEffect(() => {
    timeZoneAction.fetchTimezone();
    return () => {
      timeZoneAction.clear();
    };
  }, []);

  const genders = [
    { label: t("Male"), value: "male" },
    { label: t("Female"), value: "female" },
  ];

  function timeZoneOffset(timezoneOffset: number) {
    const decimal = timezoneOffset / 3600;
    const sign = decimal < 0 ? "-" : "+";
    const hour = Math.floor(Math.abs(decimal));
    const min = Math.floor((Math.abs(decimal) * 60) % 60);

    return (
      sign + (hour < 10 ? "0" : "") + hour + ":" + (min < 10 ? "0" : "") + min
    );
  }

  const handlePhoneModal = () => {
    setIsPhoneModalOpen((prev) => !prev);
  };

  const fileInputRef = React.useRef(null);
  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      const file = event.target.files[0];
      const filename = file.name;
      const type = "avatar";
      setAvatarLoading(true);

      const uploadResult = await upload(file, filename, type, "avatar");
      setUploadedAvatar(uploadResult.url);
      setUserState({
        ...userState,
        avatar: uploadResult.url,
        avatar_id: uploadResult.id,
        remove_avatar: false,
      });

      setAvatarLoading(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setAvatarLoading(false);
    } finally {
      setAvatarLoading(false);
    }
  };

  const updateUserData = ({
    type,
    value,
  }: {
    type: string;
    value: string | boolean;
  }) => {
    setUserState({ ...userState, [type]: value });
  };

  const sportChange = (value: string) => {
    updateUserData({ type: "sport", value });
    setSportError(false);
  };

  const UploadNewMediaRender = () => {
    return (
      <>
        <input
          type="file"
          accept="image/*"
          className={styles.file_uploader_input}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <Button
          title={t("Upload New Avatar")}
          buttonType="secondary-outline"
          buttonStyles={styles.remove_image}
          onClickFunc={handleButtonClick}
        />
      </>
    );
  };

  const userActions = useActions({ fetchSelf, editProfileSettings });

  const updateUser = async () => {
    setErrors([]);

    // Free text, so normalise here rather than in the change handler: trimming
    // as the user types would stop them ever entering "Ice Hockey".
    const sport = (userState.sport || "").trim();

    // Required for coaches only: this page is also reachable by athlete, "other"
    // and un-onboarded accounts (see AccountWrap's role list), and signup nulls
    // an empty sport for every role - gating all of them would stop a sportless
    // athlete from saving so much as a typo in their name. A /sports outage
    // never blocks saving either, because the field accepts free text.
    if (!sport && self?.kind === "coach") {
      setSportError(true);
      return;
    }

    try {
      setLoading(true);
      await userActions.editProfileSettings({ ...userState, sport: sport || null });

      try {
        await userActions.fetchSelf();
        setToastAlert(t("Your account was successfully updated."));
        setToastErrorrAlert(null);
        setTimeout(() => window.location.reload(), 500);
      } catch (err) {
        console.error("Something went wrong: ", err);
        setToastErrorrAlert(
          t(
            "An error occurred while updating your account. Please try again later.",
          ),
        );
        setToastAlert(null);
      }
    } catch (err) {
      console.error("Error updating profile: ", err);

      if (err.errors && typeof err.errors === "object") {
        setErrors(err.errors);
      } else {
        setToastErrorrAlert(
          t(
            "An error occurred while updating your account. Please try again later.",
          ),
        );
      }
      setToastAlert(null);
    } finally {
      setLoading(false);
    }
  };

  const onAvatarRemove = () => {
    setUploadedAvatar(null);
    setUserState({
      ...userState,
      avatar: null,
      avatar_id: null,
      remove_avatar: true,
    });
  };

  if (!self) {
    return null;
  }

  const loadAvatarSrc = useCallback(() => {
    if (avatarLoading) {
      return null;
    }

    if (uploadedAvatar) {
      return uploadedAvatar;
    }

    return (userState?.avatar as string) || "default";
  }, [userState?.avatar, uploadedAvatar, avatarLoading]);

  const RenderUserAvatar = () => {
    return (
      <div className={styles.avatar_container}>
        <UserAvatar
          sizeWidth={120}
          sizeHeight={120}
          altName="user"
          srcName={loadAvatarSrc()}
          userName={self.displayName}
          className={cls("avatar avatar-lg font-lg", styles.user_avatar)}
        />
        <Loading
          isLoading={avatarLoading}
          loadType="spinner"
          className={styles.avatar_loader}
        />

        {userState?.avatar && !String(userState.avatar).includes("default") && (
          <span
            className={styles.delete_icon_container}
            onClick={onAvatarRemove}
          >
            <i className={cls(styles.delete_icon, "ico ico-trash-outline")} />
          </span>
        )}
      </div>
    );
  };

  const translatedTimezones = () => {
    if (!timezones) {
      return;
    }

    return timezones
      .map((timezone: { name: string; displayName?: string; utcTotalOffset: number }) => {
        const correctTimezone: string = timezone.name.includes('Translation missing') ? timezone.name.split('.').pop() : timezone.name;
        let displayName: string = timezone.displayName ?? correctTimezone;

        if (self.locale === 'ja') {
          try {
            const parts = new Intl.DateTimeFormat("ja-JP", {
              timeZone: correctTimezone,
              timeZoneName: "long",
            }).formatToParts(new Date());
            const localizedName = parts.find((p) => p.type === "timeZoneName")?.value;
            if (localizedName) {
              displayName = localizedName;
            }
          } catch {
            // fall through with the API-provided displayName
          }
        }

        return (
          <option
            key={timezone.name}
            value={timezone.name}
            selected={timezone.name === self.timezone}
          >
            {displayName} ({correctTimezone} {timeZoneOffset(timezone.utcTotalOffset)})
          </option>
        );
      });
  };

  return (
    <AccountWrap>
      <div className={cls(styles.container, "panel")}>
        <h2 className={styles.panel_title}>{t("Profile")}</h2>
        <RenderUserAvatar />
        <UploadNewMediaRender />
        <span className={styles.input_wrapper}>
          <label htmlFor="name">{t("First Name")}</label>
          <input
            type="text"
            id="name"
            defaultValue={self.firstName}
            onChange={(e) =>
              updateUserData({ type: "first_name", value: e.target.value })
            }
          />
          {errors?.includes("first_name_empty") && (
            <p className={styles.error}>{t("Can't be blank")}</p>
          )}
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="lastName">{t("Last Name")}</label>
          <input
            type="text"
            id="lastName"
            defaultValue={self.lastName}
            onChange={(e) =>
              updateUserData({ type: "last_name", value: e.target.value })
            }
          />
          {errors?.includes("last_name_empty") && (
            <p className={styles.error}>{t("Can't be blank")}</p>
          )}
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="gender">{t("Gender")}</label>
          <select
            id="gender"
            defaultValue={self && self.gender ? self.gender : ""}
            onChange={(e) =>
              updateUserData({ type: "gender", value: e.target.value })
            }
          >
            {genders.map((gender: { label: string; value: string }) => {
              return (
                <option
                  key={gender.value}
                  defaultValue={
                    gender.value ? gender.value : t("Please Select...")
                  }
                >
                  {gender.label}
                </option>
              );
            })}
          </select>
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="sport">{t("Sport")}</label>
          <SportInput
            inputId="sport"
            value={userState.sport || ""}
            onChange={sportChange}
            hasError={sportError}
          />
          {sportError && <p className={styles.error}>{t("Can't be blank")}</p>}
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="email">{t("Email")}</label>
          <input
            type="email"
            id="email"
            defaultValue={self.email}
            onChange={(e) =>
              updateUserData({ type: "email", value: e.target.value })
            }
          />
          {errors?.includes("email_empty") && (
            <p className={styles.error}>{t("Can't be blank")}</p>
          )}
          {errors?.includes("email_already_taken") && (
            <p className={styles.error}>{t("This email is already used")}</p>
          )}
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="phone">{t("Phone")}</label>
          <AccountPhoneVerificationModal
            isPhoneModalOpen={isPhoneModalOpen}
            handlePhoneModal={handlePhoneModal}
          />
          <input
            type="text"
            id="phone"
            value={self?.phoneNormalized}
            placeholder={t("Add Phone Number")}
            onClick={handlePhoneModal}
          />
          {self.phoneVerified && self.phoneNormalized !== null && (
            <span
              className={styles.change_phone_text}
              onClick={handlePhoneModal}
            >
              {t("Change")}
            </span>
          )}
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="language">{t("Language")}</label>
          <select
            id="language"
            defaultValue={self && self.locale ? self.locale : ""}
            onChange={(e) =>
              updateUserData({ type: "locale", value: e.target.value })
            }
          >
            {languages.map((language: { label: string; value: string }) => {
              return (
                <option key={language.value} value={language.value}>
                  {language.label}
                </option>
              );
            })}
          </select>
        </span>
        <span className={styles.input_wrapper}>
          <label htmlFor="timezone">{t("Timezone")}</label>
          <select
            id="timezone"
            onChange={(e) =>
              updateUserData({
                type: "timezone",
                value: e.target.value,
              })
            }
          >
            {translatedTimezones()}
          </select>
        </span>
        <CheckBox
          testId={"analytics_checkbox"}
          name={"analytics"}
          className={styles.checkbox_wrapper}
          checked={userState.trackingOptIn}
          label={t("Allow analytics data tracking")}
          onChange={(checked) =>
            updateUserData({
              type: "trackingOptIn",
              value: checked,
            })
          }
          squareCheckIcons
        />
        {self?.kind === "coach" && hasAcademyPlan(self) && (
          <CheckBox
            testId={"auto_create_spaces_checkbox"}
            name={"auto_create_spaces"}
            className={styles.checkbox_wrapper}
            checked={userState.autoCreateSpacesOptIn}
            label={t(
              "Automatically create spaces for athletes when booking, registering or acquiring a package/subscription.",
            )}
            onChange={(checked) =>
              updateUserData({
                type: "autoCreateSpacesOptIn",
                value: checked,
              })
            }
            squareCheckIcons
          />
        )}
        <CheckBox
          testId={"assign_tags_to_resources_checkbox"}
          name={"assign_tags_to_resources"}
          className={styles.checkbox_wrapper}
          checked={userState.assignTagsToResources}
          label={t("Assign tags to resources")}
          onChange={(checked) =>
            updateUserData({
              type: "assignTagsToResources",
              value: checked,
            })
          }
          squareCheckIcons
        />
        <CheckBox
          testId={"cancel_outbound_invitation_emails_checkbox"}
          name={"cancel_outbound_invitation_emails"}
          className={styles.checkbox_wrapper}
          checked={userState.cancelOutboundInvitationEmails}
          label={t("Turn off reminder emails for space invitations. Only one invitation email will be sent.")}
          onChange={(checked) =>
            updateUserData({
              type: "cancelOutboundInvitationEmails",
              value: checked,
            })
          }
          squareCheckIcons
        />
        <Button
          buttonType="primary"
          title={t("Save Changes")}
          buttonStyles={styles.save_button}
          onClickFunc={updateUser}
          loading={loading}
        />
      </div>
      <Toast
        success={toastAlert}
        error={toastErrorAlert}
        onDone={() => setToastAlert(null)}
      />
    </AccountWrap>
  );
};

export default ProfileEdit;
